import type { ScanStage } from "@hobbyshopify/backend-client";
import { SCAN_STAGES, type StageStatus } from "../../core/primitives/scan-progress";
import { SCAN_DID_NOT_FINISH } from "../../core/services/scan-session";

/** A capture as the scanner's tray draws it. */
export interface TrayCapture {
  readonly status: "waiting" | "running" | "failed" | "ready";
  readonly progress: {
    readonly stages: Readonly<Record<ScanStage, StageStatus>>;
    readonly result?: { readonly recognitionOutcome?: "recognized" | "no_match" | "needs_photo" | null } | null;
  };
}

/** Landed with no card recognised in it, as main's Review lists such a scan apart. */
export const notRecognised = (capture: TrayCapture): boolean =>
  capture.status === "ready" && capture.progress.result?.recognitionOutcome === "no_match";

export type TraySegment = "pending" | "active" | "complete" | "failed";

/** The stage a capture is at: the one running or stopped with an error, else the next to run. */
function stageAt(stages: Readonly<Record<ScanStage, StageStatus>>): number {
  const at = SCAN_STAGES.findIndex(({ key }) => stages[key] === "active" || stages[key] === "error");
  if (at !== -1) return at;
  const next = SCAN_STAGES.findIndex(({ key }) => stages[key] === "pending");
  return next === -1 ? SCAN_STAGES.length - 1 : next;
}

/** The four-part bar under a card in the tray, as iOS's `AdminPendingScan.state(of:)`. */
export function traySegments(capture: TrayCapture): TraySegment[] {
  if (capture.status === "ready") return SCAN_STAGES.map(() => "complete");
  if (capture.status === "waiting") return SCAN_STAGES.map(() => "pending");
  const at = stageAt(capture.progress.stages);
  const here: TraySegment = capture.status === "failed" ? "failed" : "active";
  return SCAN_STAGES.map((_, i) => (i < at ? "complete" : i > at ? "pending" : here));
}

/** The word under the bar, as iOS's tray: "Failed · retry", "Not recognised", "Ready", "Waiting", else the stage running. */
export function trayWord(capture: TrayCapture): string {
  if (capture.status === "failed") return "Failed · retry";
  if (notRecognised(capture)) return "Not recognised";
  if (capture.status === "ready") return "Ready";
  if (capture.status === "waiting") return "Waiting";
  return SCAN_STAGES[stageAt(capture.progress.stages)]?.label ?? "";
}

/** A capture as Review and the customer's page list it while it is on its way, as iOS's `AdminPendingScanRow`. */
export interface PendingCapture extends TrayCapture {
  readonly error: string | null;
}

/** The row's title: "Scan failed", "Card could not be recognised", "Queued" or "Scanning…". */
export function pendingTitle(capture: PendingCapture): string {
  if (capture.status === "failed") return "Scan failed";
  if (notRecognised(capture)) return "Card could not be recognised";
  return capture.status === "waiting" ? "Queued" : "Scanning…";
}

/**
 * The line under it, as iOS's `statusText`: the error, main's advice for a card not recognised, "Ready", "Waiting to
 * scan…", else the stage running.
 */
export function pendingCaption(capture: PendingCapture): string {
  if (capture.status === "failed") return capture.error ?? SCAN_DID_NOT_FINISH;
  if (notRecognised(capture)) return "Upload a new photo with the whole card in frame and even lighting.";
  if (capture.status === "ready") return "Ready";
  if (capture.status === "waiting") return "Waiting to scan…";
  return SCAN_STAGES[stageAt(capture.progress.stages)]?.label ?? "";
}

/** "2 ready for review", and the cards no card was recognised in apart: "· 1 not recognised". */
export function readyLine(ready: number, unrecognised: number): string {
  return unrecognised === 0
    ? `${ready} ready for review`
    : `${ready} ready for review · ${unrecognised} not recognised`;
}

/** "THIS SESSION · 1 CARD", as iOS's tray header. */
export function sessionHeader(count: number): string {
  return `This session · ${count} ${count === 1 ? "card" : "cards"}`.toUpperCase();
}
